/**
 * Voice of X — ``ReportNative`` Zod schema + inferred type.
 *
 * Workstream B of the Voice of X Analytics plan
 * (``ando-agent/tasks/voice_of_x_analytics_plan.md``, §2 schema target,
 * M0.4). The Ando-native superset that extends the existing ingest
 * ``ReportDoc`` shape with everything the generation pipeline needs to be
 * production-grade: citation grounding (eTracer / DataSTORM pattern),
 * pipeline cost + telemetry, taxonomy with HyperMem semantic-memory link,
 * brand lock, accessibility audit, EU AI Act Art. 50 disclosure (machine-
 * readable JSON-LD + human-readable banner, deadline 2 Aug 2026),
 * evaluation scores (deterministic + LLM-judge + Patronus Lynx 5-dim RAG),
 * semantic-diff versioning, and the draft → reviewed → final lifecycle.
 *
 * ``ReportDoc`` source of truth: TypeScript interface at
 *   ``apps/client/src/features/report/types.ts`` (lines 13–73).
 *
 * That interface is the existing ingest schema — kept for compatibility
 * with reports IMPORTED from external sources (e.g. the pt-BR Rio de
 * Janeiro POC). For reports GENERATED natively by Ando we mirror its
 * shape here as ``ReportDocBaseSchema`` (the apps/client interface stays
 * structurally compatible with ``z.infer<typeof ReportDocBaseSchema>``)
 * and extend it with the additional fields. The base mirror lives in the
 * same file rather than ``apps/client`` to keep the @ando/core domain
 * layer framework-agnostic — apps/client imports core, never the other
 * way around (see ``index.ts`` barrel header).
 *
 * Pydantic mirror: ``ando-agent/src/models/voice_report.py`` — field
 * names + literal sets must stay byte-for-byte identical.
 */

import { z } from "zod";

import { IsoTimestampSchema, UuidSchema } from "./common.js";

// ---------------------------------------------------------------------------
// BCP-47 language tag — restricted regex matching the Pydantic mirror
// pattern. Accepts ``it``, ``en``, ``pt-BR``, ``zh-Hant``, ``en-US-x-foo``…
// (the trailing optional region/variant subtag covers the common cases the
// Voice of X pipeline emits; full RFC 5646 grammar is intentionally out of
// scope — see plan §2).
// ---------------------------------------------------------------------------

export const Bcp47LanguageSchema = z
  .string()
  .regex(
    /^[a-zA-Z]{2,3}(-[a-zA-Z]{2,4})?(-[a-zA-Z0-9]{2,4})?$/,
    "must be a BCP-47 language tag (e.g. 'it', 'en-US', 'pt-BR')",
  );
export type Bcp47Language = z.infer<typeof Bcp47LanguageSchema>;

/**
 * Normalized [0, 1] score — used for every confidence / probability field
 * so the constraint stays in one place. Pattern mirrors
 * ``BaseDisplayFieldsSchema.confidence`` in ``displays.ts:84``.
 */
export const NormalizedScoreSchema = z.number().min(0).max(1);
export type NormalizedScore = z.infer<typeof NormalizedScoreSchema>;

// ---------------------------------------------------------------------------
// ReportDoc base mirror — see file header for rationale. Field-for-field
// mirror of ``apps/client/src/features/report/types.ts`` (lines 13–73).
// ---------------------------------------------------------------------------

const ReportSurveyInfoSchema = z.object({
  title: z.string().optional(),
  company: z.string().optional(),
  period: z.string().optional(),
  totalRespondents: z.number().optional(),
  responseRate: z.number().optional(),
  questions: z.number().optional(),
  nps: z.number().optional(),
  npsCategory: z.string().optional(),
});

/**
 * Strict enum of widget types renderable by ``ReportComponent`` (apps/client
 * ``features/report/components/ReportComponent.tsx``). Kept as a permissive
 * lookup surface — ``ReportComponentNodeSchema.type`` stays ``z.string()`` so
 * legacy POC imports with unknown future types still parse (the renderer
 * falls back to "render nothing" for unknowns, see ReportComponent.tsx:160).
 * This enum is the source of truth for the FE renderer dispatch + for the
 * BE outline composer's ``RequestedWidget`` union (plan M4.5).
 *
 * NEW M4.4 widgets:
 *   - respondentQuotesCarousel (verbatim quote slider with sentiment border)
 *   - categoryEmergenceTimeline (lollipop chart of first-appearance per run)
 *   - npsPromoterDetractorGrid (3-col Detr/Pass/Promo with icon redundancy)
 *   - comparisonGrid (side-by-side segment A vs B with delta badges)
 *   - aiDisclosureBanner (renders the existing AiDisclosureBanner inline)
 *   - confidenceMeter (linear meter with role="meter" + WCAG values)
 */
export const VoiceReportComponentTypeSchema = z.enum([
  // chrome / containers
  "card",
  "h3",
  "container",
  "grid-container",
  // existing charts + tables
  "barChart",
  "sentimentDivergentChart",
  "sentimentThreeColorChart",
  "sentimentImpactTable",
  "positiveCategoriesTable",
  "negativeCategoriesTable",
  "segmentationTable",
  "recommendationsTable",
  // NEW M4.4 widgets
  "respondentQuotesCarousel",
  "categoryEmergenceTimeline",
  "npsPromoterDetractorGrid",
  "comparisonGrid",
  "aiDisclosureBanner",
  "confidenceMeter",
]);
export type VoiceReportComponentType = z.infer<typeof VoiceReportComponentTypeSchema>;

/**
 * Recursive component node. ``components`` self-references, hence the
 * ``z.lazy`` indirection (Zod 4 still requires the explicit type
 * annotation on the schema variable for the recursion to type-check —
 * see https://zod.dev/?id=recursive-types).
 */
type ReportComponentNodeShape = {
  type: string;
  index?: number;
  title?: string;
  text?: string;
  cardStyleVariant?: string;
  cardContentVariant?: string;
  titleStyleVariant?: string;
  useDescription?: boolean;
  dataPath?: string;
  config?: Record<string, unknown>;
  components?: ReportComponentNodeShape[];
};

const ReportComponentNodeSchema: z.ZodType<ReportComponentNodeShape> = z.lazy(
  () =>
    z.object({
      type: z.string(),
      index: z.number().optional(),
      title: z.string().optional(),
      text: z.string().optional(),
      cardStyleVariant: z.string().optional(),
      cardContentVariant: z.string().optional(),
      titleStyleVariant: z.string().optional(),
      useDescription: z.boolean().optional(),
      dataPath: z.string().optional(),
      config: z.record(z.string(), z.unknown()).optional(),
      components: z.array(ReportComponentNodeSchema).optional(),
    }),
);

const ReportQuestionSchema = z.object({
  question_id: z.string(),
  index: z.number(),
  question: z.string(),
  summary: z.string().optional(),
  questionType: z.string().optional(),
  id: z.number().optional(),
  data: z.record(z.string(), z.unknown()).optional(),
});

const ReportSubsectionSchema = z.object({
  id: z.string(),
  index: z.number(),
  name: z.string(),
  icon: z.string().optional(),
  components: z.array(ReportComponentNodeSchema).optional(),
  data: z.record(z.string(), z.unknown()).optional(),
});

const ReportSectionSchema = z.object({
  id: z.string(),
  index: z.number(),
  name: z.string(),
  icon: z.string().optional(),
  subsections: z.array(ReportSubsectionSchema).optional(),
  questions: z.array(ReportQuestionSchema).optional(),
  components: z.array(ReportComponentNodeSchema).optional(),
  data: z.record(z.string(), z.unknown()).optional(),
});

/**
 * ReportDoc base mirror — structurally identical to
 * ``apps/client/src/features/report/types.ts::ReportDoc``. Both that
 * interface and ``ReportNative`` here extend this shape.
 */
export const ReportDocBaseSchema = z.object({
  metadata: z
    .object({
      version: z.string().optional(),
      language: z.string().optional(),
      surveyId: z.string().optional(),
    })
    .optional(),
  sections: z.array(ReportSectionSchema),
  uiTexts: z.record(z.string(), z.unknown()).optional(),
  surveyInfo: ReportSurveyInfoSchema.optional(),
});
export type ReportDocBase = z.infer<typeof ReportDocBaseSchema>;

// ---------------------------------------------------------------------------
// Native superset fields — plan §2
// ---------------------------------------------------------------------------

/**
 * Sentence-level citation (eTracer / DataSTORM pattern). ``is_correct``
 * (the citation EXISTS in the source dataset) and ``is_faithful`` (the
 * model actually derived the claim from the cited source) are tracked
 * separately — 57% of "correct" citations miss faithfulness (claim from
 * the June 17 deep-research, plan §6).
 *
 * Named ``VoiceCitation`` to avoid collision with the chat-surface
 * ``Citation`` in ``message.ts`` (a different concept — that one is the
 * assistant-message-level source ref).
 */
export const VoiceCitationSchema = z.object({
  respondent_id: z.string().min(1),
  offset_start: z.number().int().nonnegative(),
  offset_end: z.number().int().nonnegative(),
  quote: z.string(),
  is_correct: z.boolean(),
  is_faithful: z.boolean(),
});
export type VoiceCitation = z.infer<typeof VoiceCitationSchema>;

/**
 * Per-stage telemetry of the 7-stage pipeline (plan §3). Cost is tracked
 * separately per stage so the FE can surface the post-mortem breakdown
 * without re-aggregating from the model log.
 */
export const PipelineStageMetaSchema = z.object({
  stage_name: z.string().min(1),
  started_at: IsoTimestampSchema,
  completed_at: IsoTimestampSchema,
  status: z.enum(["pending", "running", "completed", "failed", "skipped"]),
  cost_tokens_in: z.number().int().nonnegative(),
  cost_tokens_out: z.number().int().nonnegative(),
  model: z.string().min(1),
});
export type PipelineStageMeta = z.infer<typeof PipelineStageMetaSchema>;

/**
 * Discovered category from Stage 2 (Taxonomy Extraction). Hierarchy is
 * optional — ``parent_category_id`` enables nested taxonomies when the
 * dataset warrants them. ``seed_phrases`` are the canonical phrasings
 * the clustering agent locked onto (used both for explainability in the
 * UI and as retrieval anchors at re-classification time).
 */
export const CategorySchema = z.object({
  id: UuidSchema,
  name: z.string().min(1),
  definition: z.string(),
  seed_phrases: z.array(z.string()).default([]),
  parent_category_id: UuidSchema.optional(),
});
export type Category = z.infer<typeof CategorySchema>;

/**
 * Column metadata from schema discovery (riuso ``schema_profiler.py``
 * end-to-end, plan §1.1). ``is_open_text`` is the gating flag the
 * narrative composer uses to pick which columns to mine for verbatims.
 */
export const ColumnDefSchema = z.object({
  name: z.string().min(1),
  kind: z.string().min(1),
  sample_values: z.array(z.unknown()).optional(),
  is_open_text: z.boolean(),
});
export type ColumnDef = z.infer<typeof ColumnDefSchema>;

const SamplingStrategySchema = z.object({
  strategy: z.string().min(1),
  seed: z.number().int(),
  sample_size: z.number().int().nonnegative(),
});

/**
 * Discriminator for the scope source:
 *   * ``ando_knowledge_full`` (default) — the dataset spans ALL of
 *     Ando's existing knowledge (Weaviate + HyperMem + connectors).
 *   * ``ando_knowledge_scoped`` — a non-empty ``scope_spec`` narrows
 *     the span.
 *   * ``ando_spreadsheet_connector`` — single connector-ingested table
 *     in ``spreadsheet_tables``.
 *   * ``external_upload`` — DEFERRED to M2+ (forward-compatible literal
 *     for one-off CSV/XLSX uploads not behind any connector).
 */
export const VoiceDatasetSourceKindSchema = z.enum([
  "ando_knowledge_full",
  "ando_knowledge_scoped",
  "ando_spreadsheet_connector",
  "external_upload",
]);
export type VoiceDatasetSourceKind = z.infer<typeof VoiceDatasetSourceKindSchema>;

/**
 * Immutable scope-snapshot pointer to the dataset at generation time.
 *
 * REWRITE #2 (June 17, 2026): the dataset spans ALL of Ando's existing
 * knowledge by default (``source_kind='ando_knowledge_full'`` +
 * ``scope_spec={}``). ``scope_spec`` is OPT-IN narrowing — when
 * populated, ``source_kind`` flips to ``ando_knowledge_scoped`` and the
 * verifier replays the same filter at fact-check time so citations
 * stay stable even as Weaviate ingests new chunks.
 *
 * Upload-specific fields (``source_uri``, ``mime``, ``bucket``,
 * ``minio_pointer``) are DELIBERATELY ABSENT — the M2+ upload fallback
 * stamps ``source_kind='external_upload'`` and surfaces those bytes
 * via a sibling schema; the migration keeps the columns nullable so
 * the FE can land that surface without a schema reshuffle.
 */
export const SourceDatasetMetaSchema = z.object({
  dataset_id: UuidSchema,
  name: z.string().min(1),
  source_kind: VoiceDatasetSourceKindSchema.default("ando_knowledge_full"),
  scope_spec: z.record(z.string(), z.unknown()).default({}),
  source_collection_refs: z.array(z.string()).default([]),
  snapshot_at: IsoTimestampSchema,
  estimated_n_chunks: z.number().int().nonnegative().nullable().optional(),
  n_respondents: z.number().int().nonnegative().nullable().optional(),
  n_responses: z.number().int().nonnegative().nullable().optional(),
  language_detected: Bcp47LanguageSchema.nullable().optional(),
  columns_schema: z.array(ColumnDefSchema).default([]),
  sampling: SamplingStrategySchema.nullable().optional(),
});
export type SourceDatasetMeta = z.infer<typeof SourceDatasetMetaSchema>;

export const GenerationMetaSchema = z.object({
  engine: z.string().min(1),
  engine_version: z.string().min(1),
  model: z.string().min(1),
  generated_at: IsoTimestampSchema,
  generated_by: UuidSchema,
  dataset_snapshot_id: UuidSchema,
  cost_eur: z.number().nonnegative(),
  pipeline_stages: z.array(PipelineStageMetaSchema),
});
export type GenerationMeta = z.infer<typeof GenerationMetaSchema>;

/**
 * Cross-run taxonomy meta. ``semantic_memory_link`` points at the
 * HyperMem semantic_memory row that lets the NEXT report for the same
 * tenant + dataset_kind start from the discovered baseline (plan §1.4).
 * Optional because the first run for a tenant has nothing to link to.
 */
export const TaxonomyMetaSchema = z.object({
  categories: z.array(CategorySchema),
  extraction_run_id: UuidSchema,
  classification_confidence_p50: NormalizedScoreSchema,
  semantic_memory_link: UuidSchema.optional(),
});
export type TaxonomyMeta = z.infer<typeof TaxonomyMetaSchema>;

const BrandPaletteSchema = z.object({
  primary: z.string().min(1),
  secondary: z.string().min(1),
  accent: z.string().min(1),
  neutral: z.string().min(1),
});

const BrandTypographySchema = z.object({
  heading: z.string().min(1),
  body: z.string().min(1),
});

/**
 * Brand lock — pinned at generation time so a Brand Kit change later
 * doesn't silently retroactively re-style historical reports. ``logo_id``
 * is the MinIO/Drive ref for the brand logo asset.
 */
export const BrandLockSchema = z.object({
  palette: BrandPaletteSchema,
  typography: BrandTypographySchema,
  logo_id: z.string().optional(),
});
export type BrandLock = z.infer<typeof BrandLockSchema>;

/**
 * Accessibility gate output (plan M5.5 — WCAG 2.2 AA + Axe in CI).
 */
export const AccessibilityMetaSchema = z.object({
  wcag_22_aa: z.boolean(),
  alt_texts_count: z.number().int().nonnegative(),
  contrast_check: z.enum(["pass", "warn", "fail"]),
});
export type AccessibilityMeta = z.infer<typeof AccessibilityMetaSchema>;

/**
 * EU AI Act Art. 50 disclosure (deadline 2 Aug 2026 — plan §0).
 *
 * - ``machine_readable``: JSON-LD object (schema.org/CreativeWork +
 *   ``aiGenerated: true`` + ``disclosureBasis``). Kept as a permissive
 *   ``Record<string, unknown>`` because the JSON-LD shape is checked at
 *   output time, not at schema parse time.
 * - ``human_readable``: BCP-47 → localized banner string. Always carries
 *   at least IT + EN + the dataset language (plan M5.1).
 * - ``reduction_basis``: optional Art. 50 carve-out signaling human
 *   editorial review (reduces disclosure obligations — plan §8).
 */
export const AiDisclosureSchema = z.object({
  is_ai_generated: z.literal(true),
  machine_readable: z.record(z.string(), z.unknown()),
  human_readable: z.record(Bcp47LanguageSchema, z.string()),
  reduction_basis: z.enum(["human_editorial_review"]).optional(),
});
export type AiDisclosure = z.infer<typeof AiDisclosureSchema>;

const DeterministicScoresSchema = z.object({
  citation_presence: z.boolean(),
  format_adherence: z.boolean(),
  confidence: NormalizedScoreSchema,
});

const LlmJudgeScoresSchema = z.object({
  rubric_id: z.string().min(1),
  factuality_score: NormalizedScoreSchema,
  recurring_calibration_at: IsoTimestampSchema,
});

/**
 * Patronus Lynx 5-dimensional RAG verification (plan M1.5). The
 * dimensions are tracked separately so the failure surface points the
 * verifier agent at the right re-run (e.g. low ``context_relevance`` →
 * widen retrieval; low ``groundedness`` → tighten citation enforcement).
 */
const GroundednessRagSchema = z.object({
  lynx_score: NormalizedScoreSchema,
  five_dimensions: z.object({
    context_relevance: NormalizedScoreSchema,
    groundedness: NormalizedScoreSchema,
    answer_relevance: NormalizedScoreSchema,
    citation_validity: NormalizedScoreSchema,
    contradiction: NormalizedScoreSchema,
  }),
});

/**
 * Cascade evaluation (plan §5.3 best-practice). ``ece`` (Expected
 * Calibration Error) gates the Multi-Agent Reflexion stage (plan §1.6).
 * ``human_review_required`` is the final hand-off flag the UI uses to
 * route to the reviewer queue before ``draft_lifecycle`` can advance.
 */
export const EvaluationScoresSchema = z.object({
  deterministic: DeterministicScoresSchema,
  llm_judge: LlmJudgeScoresSchema,
  groundedness_rag: GroundednessRagSchema,
  ece: z.number().nonnegative(),
  human_review_required: z.boolean(),
});
export type EvaluationScores = z.infer<typeof EvaluationScoresSchema>;

/**
 * JSON-Patch-style semantic diff op (plan §3.6 — timeline between runs).
 * ``path`` is a JSON Pointer (RFC 6901) into the report tree.
 */
export const SemanticDiffOpSchema = z.object({
  op: z.enum(["add", "remove", "replace"]),
  path: z.string().min(1),
  value: z.unknown().optional(),
});
export type SemanticDiffOp = z.infer<typeof SemanticDiffOpSchema>;

export const VersioningMetaSchema = z.object({
  run_id: UuidSchema,
  parent_run_id: UuidSchema.optional(),
  semantic_diff: z.array(SemanticDiffOpSchema),
});
export type VersioningMeta = z.infer<typeof VersioningMetaSchema>;

/**
 * Draft → reviewed → final lifecycle (EU AI Act Art. 50 carve-out —
 * plan M5.3). Crossing into ``final`` requires an upstream
 * ``human_editorial_review`` decision; the FE flow gates the transition
 * on ``evaluation_scores.human_review_required === false``.
 */
export const DraftLifecycleSchema = z.enum(["draft", "reviewed", "final"]);
export type DraftLifecycle = z.infer<typeof DraftLifecycleSchema>;

// ---------------------------------------------------------------------------
// ReportNative — base + all native superset fields
// ---------------------------------------------------------------------------

/**
 * The native Ando shape produced by ``GenerateVoiceXReportWorkflow`` (plan
 * §3). Everything beyond ``ReportDocBaseSchema`` is REQUIRED for
 * production-grade output — ``citations`` is non-optional because
 * Workstream B plan M5.7 calls for runtime rejection when a narrative
 * card has no citation backing. The two opt-in nested fields preserve
 * their per-field optionality (``semantic_memory_link``, ``logo_id``,
 * ``reduction_basis``, ``parent_run_id``, etc.).
 */
export const ReportNativeSchema = ReportDocBaseSchema.extend({
  generation: GenerationMetaSchema,
  source_dataset: SourceDatasetMetaSchema,
  taxonomy: TaxonomyMetaSchema,
  citations: z.array(VoiceCitationSchema),
  draft_lifecycle: DraftLifecycleSchema,
  brand_lock: BrandLockSchema,
  accessibility_meta: AccessibilityMetaSchema,
  ai_disclosure: AiDisclosureSchema,
  evaluation_scores: EvaluationScoresSchema,
  versioning: VersioningMetaSchema,
});
export type ReportNative = z.infer<typeof ReportNativeSchema>;
